import React, { useState } from "react";
import { Box, IconButton, TextField, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "../../localization/useTranslation";
import { AiChatConversationListItem } from "../AiChat.types";
import { useAiChatConversationsQuery, useRenameAiChatConversationMutation } from "./AiChatSidebar.hooks";
import { useAiChatSidebarStyles } from "./AiChatSidebar.styles";
import DeleteAiChatConversationDialog from "../deleteAiChatConversationDialog/DeleteAiChatConversationDialog";

interface AiChatSidebarProps {
    activeConversationId: string | null;
    onSelectConversation: (id: string) => void;
    onNewChat: () => void;
    onActiveConversationDeleted: () => void;
}

function AiChatSidebar({ activeConversationId, onSelectConversation, onNewChat, onActiveConversationDeleted }: AiChatSidebarProps) {
    const { translateString, translateDate } = useTranslation();
    const styles = useAiChatSidebarStyles();
    const { data } = useAiChatConversationsQuery();
    const renameMutation = useRenameAiChatConversationMutation();

    const [searchQuery, setSearchQuery] = useState("");
    const [editingId, setEditingId] = useState<string | null>(null);
    const [conversationToDelete, setConversationToDelete] = useState<AiChatConversationListItem | null>(null);

    const conversations = data ?? [];
    const filtered = searchQuery.trim() ? conversations.filter((c) => c.title.toLowerCase().includes(searchQuery.trim().toLowerCase())) : conversations;

    function startRename(item: AiChatConversationListItem, e: React.MouseEvent) {
        e.stopPropagation();
        setEditingId(item.id);
    }

    function cancelRename() {
        setEditingId(null);
    }

    function commitRename(id: string, title: string) {
        const trimmed = title.trim();
        if (!trimmed) {
            cancelRename();
            return;
        }
        renameMutation.mutate({ id, title: trimmed });
        cancelRename();
    }

    return (
        <Box sx={styles.sidebar}>
            <Box component="button" sx={styles.newChatButton} onClick={onNewChat}>
                <AddIcon sx={{ fontSize: 16 }} />
                {translateString("aiChatNewChat")}
            </Box>

            <Box sx={styles.searchBox}>
                <TextField
                    fullWidth
                    size="small"
                    placeholder={translateString("aiChatSearchConversations")}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    slotProps={{ input: { startAdornment: <SearchIcon sx={{ fontSize: 16, color: "text.disabled", mr: 1 }} /> } }}
                />
            </Box>

            <Typography sx={styles.listLabel}>{translateString("aiChatRecentChats")}</Typography>

            <Box sx={styles.list}>
                {filtered.length === 0 ? (
                    <Box sx={styles.emptyState}>{translateString("aiChatNoConversations")}</Box>
                ) : (
                    filtered.map((item) => {
                        const isActive = item.id === activeConversationId;
                        const isEditing = editingId === item.id;
                        return (
                            <Box
                                key={item.id}
                                sx={[styles.listItem, isActive && styles.listItemActive]}
                                onClick={() => !isEditing && onSelectConversation(item.id)}
                            >
                                <Box sx={styles.listItemText}>
                                    {isEditing ? (
                                        <RenameRow
                                            initialTitle={item.title}
                                            onCommit={(title) => commitRename(item.id, title)}
                                            onCancel={cancelRename}
                                            styles={styles}
                                            saveLabel={translateString("save")}
                                            cancelLabel={translateString("cancel")}
                                        />
                                    ) : (
                                        <>
                                            <Typography sx={styles.listItemTitle}>{item.title}</Typography>
                                            <Typography sx={styles.listItemDate}>{translateDate(new Date(item.updatedAt))}</Typography>
                                        </>
                                    )}
                                </Box>
                                {!isEditing && (
                                    <Box className="ai-chat-row-actions" sx={styles.rowActions}>
                                        <IconButton size="small" onClick={(e) => startRename(item, e)} title={translateString("aiChatRenameConversation")}>
                                            <EditIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            disabled={item.hasProcessingAttachments}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setConversationToDelete(item);
                                            }}
                                            title={
                                                item.hasProcessingAttachments
                                                    ? translateString("aiChatDeleteBlockedByProcessing")
                                                    : translateString("aiChatDeleteConversation")
                                            }
                                        >
                                            <DeleteIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    </Box>
                                )}
                            </Box>
                        );
                    })
                )}
            </Box>

            {conversationToDelete && (
                <DeleteAiChatConversationDialog
                    conversation={conversationToDelete}
                    activeConversationId={activeConversationId}
                    onDialogClose={() => setConversationToDelete(null)}
                    onActiveConversationDeleted={onActiveConversationDeleted}
                />
            )}
        </Box>
    );
}

interface RenameRowProps {
    initialTitle: string;
    onCommit: (title: string) => void;
    onCancel: () => void;
    styles: ReturnType<typeof useAiChatSidebarStyles>;
    saveLabel: string;
    cancelLabel: string;
}

function RenameRow({ initialTitle, onCommit, onCancel, styles, saveLabel, cancelLabel }: RenameRowProps) {
    const [title, setTitle] = useState(initialTitle);

    return (
        <Box sx={styles.renameRow}>
            <TextField
                sx={styles.renameInput}
                size="small"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        e.preventDefault();
                        onCommit(title);
                    } else if (e.key === "Escape") {
                        e.preventDefault();
                        onCancel();
                    }
                }}
                autoFocus
            />
            <IconButton
                size="small"
                onClick={(e) => {
                    e.stopPropagation();
                    onCommit(title);
                }}
                title={saveLabel}
                sx={styles.renameConfirmButton}
            >
                <CheckIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <IconButton
                size="small"
                onClick={(e) => {
                    e.stopPropagation();
                    onCancel();
                }}
                title={cancelLabel}
                sx={styles.renameCancelButton}
            >
                <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
        </Box>
    );
}

export default AiChatSidebar;
